import axios from 'axios';

// Set global defaults on raw axios calls
axios.defaults.withCredentials = true;

// Create axios instance
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1',
  timeout: process.env.TIMEOUT,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true
});

// Request interceptor helper to add tenant ID dynamically
const requestHandler = (config) => {
  config.withCredentials = true;

  // Clean up legacy manual Bearer tokens unconditionally to let cookies handle auth
  if (config.headers) {
    delete config.headers.Authorization;
    delete config.headers.authorization;
    config.headers['X-Portal-Source'] = 'client';
  }

  // Try to get tenant ID from stored user or tenant info
  try {
    const tenantStr = localStorage.getItem('tenant');
    if (tenantStr) {
      const tenant = JSON.parse(tenantStr);
      if (tenant?.id) {
        config.headers['x-tenant-id'] = tenant.id;
      }
    } else {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        if (user?.tenantId) {
          config.headers['x-tenant-id'] = user.tenantId;
        }
      }
    }
  } catch (e) {
    console.error('Error extracting tenant ID in interceptor:', e);
  }
  return config;
};

// Register request interceptors
api.interceptors.request.use(requestHandler, (error) => Promise.reject(error));
axios.interceptors.request.use(requestHandler, (error) => Promise.reject(error));

// Token refresh state
let isRefreshing = false;
let pendingRequests = [];

const processQueue = (error) => {
  pendingRequests.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve();
    }
  });
  pendingRequests = [];
};

const REFRESH_TRIGGER_CODES = ['TOKEN_EXPIRED', 'NO_TOKEN'];

// Response interceptor helper
const createResponseInterceptor = (axiosInstance) => {
  return [
    (response) => {
      const url = response.config?.url;
      const method = response.config?.method?.toLowerCase();
      if (url && (method === 'post' || method === 'put' || method === 'delete')) {
        if (url.includes('/caregivers') || url.includes('/patient') || url.includes('/users')) {
          window.dispatchEvent(new Event('subscription-update'));
        }
      }
      return response;
    },
    async (error) => {
      const originalRequest = error.config;
      const errorCode = error.response?.data?.error?.code;
      const status    = error.response?.status;

      // Handle token expiration/missing token and attempt silent refresh
      if (status === 401 && REFRESH_TRIGGER_CODES.includes(errorCode) && !originalRequest._retried) {
        originalRequest._retried = true;

        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            pendingRequests.push({ resolve, reject });
          })
            .then(() => axiosInstance(originalRequest))
            .catch((err) => Promise.reject(err));
        }

        isRefreshing = true;

        try {
          // Send the clientRefreshToken httpOnly cookie automatically to refresh access token
          await axios.post(
            `${process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1'}/auth/client/refresh-token`,
            {},
            { withCredentials: true }
          );

          processQueue(null);
          isRefreshing = false;

          // Retry the original request
          return axiosInstance(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError);
          isRefreshing = false;

          // Clear auth data and redirect to login
          localStorage.removeItem('user');
          localStorage.removeItem('tenant');
          localStorage.removeItem('token');
          sessionStorage.removeItem('user');
          sessionStorage.removeItem('tenant');
          sessionStorage.removeItem('token');

          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
          return Promise.reject(refreshError);
        }
      }

      // Non-recoverable 401
      if (status === 401 && window.location.pathname !== '/login') {
        localStorage.removeItem('user');
        localStorage.removeItem('tenant');
        localStorage.removeItem('token');
        sessionStorage.removeItem('user');
        sessionStorage.removeItem('tenant');
        sessionStorage.removeItem('token');
        window.location.href = '/login';
      }

      return Promise.reject(error);
    }
  ];
};

// Register response interceptors
const [successHandlerApi, errorHandlerApi] = createResponseInterceptor(api);
api.interceptors.response.use(successHandlerApi, errorHandlerApi);

const [successHandlerAxios, errorHandlerAxios] = createResponseInterceptor(axios);
axios.interceptors.response.use(successHandlerAxios, errorHandlerAxios);

export default api;